<template>
  <div class="environments" v-if="loadData" :class="{ 'read-widget': mode === 'read' }">
    <div class="flex items-center justify-between q-mb-md block-label">
      <span class="label-block">{{
        registrationData.infrastructureServiceApm === 'IAASSC' ? 'Swisscom SCO APIs:' : 'Environments:'
      }}</span>
      <q-btn
        v-if="mode === 'edit'"
        flat
        no-caps
        class="q-ml-md btn--no-hover text-weight-regular"
        round
        color="light-blue-5"
        size="15px"
        style="min-height: 30px"
        :disabled="Boolean(addEnvError?.length) || showForm"
        :ripple="false"
        @click="showForm = true"
      >
        + {{ registrationData.infrastructureServiceApm === 'IAASSC' ? 'Enable API' : 'Add Environment' }}
        <q-tooltip v-if="addEnvError?.length">{{ addEnvError }}</q-tooltip>
      </q-btn>
    </div>
    <div v-if="showForm" class="env-form hide-label">
      <div class="flex items-center no-wrap">
        <div class="full-width q-px-md">
          <q-banner v-if="generalErrors.length" class="text-white approval-error-banner q-mt-sm q-mb-sm">
            <span class="flex items-center" v-for="(error, key) in generalErrors" :key="key">
              <q-icon name="error" class="q-mr-xs" size="16px"></q-icon>{{ error.message }}
            </span>
          </q-banner>
          <json-forms
            :data="formData"
            :renderers="renderers"
            :schema="schema"
            :uischema="uiSchema"
            @change="onChange"
          />
        </div>

        <div class="btns flex items-center justify-end q-ml-auto q-mb-sm">
          <q-btn
            @click="send"
            no-caps
            text-color="dark"
            :loading="loadSend"
            :disabled="
              environments.length === formData.environments.length ||
              storedEnvironments().length === formData.environments.length ||
              loadSend ||
              submitDisable ||
              Boolean(formErrors.length) ||
              Boolean(errors.length) ||
              Boolean(generalErrors.length)
            "
            color="primary"
            >Save</q-btn
          >
          <q-btn @click="cancel" class="btn--no-hover" no-caps flat>Cancel</q-btn>
        </div>
      </div>
      <div v-if="summary?.composition?.length && !loadSummary && showSummary" class="q-px-md q-mb-md">
        <div class="summary">{{ summary?.summaryText }}</div>
        <ul class="roles">
          <li v-for="(item, index) in summary?.composition" :key="index">
            {{ item.displayName }}
          </li>
        </ul>
      </div>
    </div>
    <ul v-if="environments.length || storedEnvironments().length">
      <li class="q-mb-xs" v-for="(environment, key) in environments" :key="key">
        {{ environment.value }}
        <StatusCell showLabel :status="environment.status" class="q-ml-auto" />
        <WorkflowsBtn
          class="q-ml-md"
          @showWorkflow="showWorkflow($event, environment.id)"
          :relatedWorkflowStatuses="relatedWorkflowStatuses.find((i) => i.id === environment.id)?.statuses || []"
        />
        <ondemand-button
          v-if="mode === 'edit' && isShowOndemandOffboard"
          label="Offboard"
          icon="sym_o_delete"
          @lockClick="validateOffboard(environment)"
          @unlockClick="openConfirmationPopUp(environment)"
          :loading="loadEnv === environment.value"
          :state="unlockEnvsForOffboard.includes(environment.value) ? 'unlock' : 'lock'"
          :errorMessage="disableOffboardEnvs[environment.value]"
          class="q-ml-sm"
        />
        <q-btn
          v-if="mode === 'edit' && !isShowOffboard"
          class="btn--no-hover q-ml-sm"
          size="12px"
          icon="sym_o_delete"
          :disabled="disableOffboardEnvs[environment.value]"
          flat
          padding="0"
          :ripple="false"
          @click="openConfirmationPopUp(environment)"
        >
          <q-tooltip v-if="disableOffboardEnvs[environment.value]" style="white-space: pre-line">{{
            disableOffboardEnvs[environment.value]
          }}</q-tooltip>
          <q-tooltip v-else>Offboard</q-tooltip>
        </q-btn>
      </li>
      <li class="q-mb-xs" v-for="(environment, key) in storedEnvironments()" :key="key">
        {{ environment }}
        <q-spinner size="18px" class="q-ml-auto"> </q-spinner>
        <q-tooltip>Workflow in the process of creation... </q-tooltip>
      </li>
    </ul>

    <q-dialog v-model="confirmOffboardPopup" class="single-service-page" persistent>
      <q-card style="min-width: 768px; border-radius: 0px">
        <q-toolbar class="shadow-2 q-py-sm q-px-md">
          <q-toolbar-title class="text-weight-bold">
            <strong>Are you sure?</strong>
          </q-toolbar-title>
          <q-btn icon="close" round flat class="btn--no-hover" @click="confirmOffboardPopup = false"></q-btn>
        </q-toolbar>
        <q-card-section class="q-mx-md q-px-none q-pb-none" style="border-top: 2px solid var(--q-primary)">
          <p>
            You are about to offboard <strong>{{ registrationData.infrastructureServiceApm }}</strong> from
            <strong>{{ registrationData.digitalServiceApm }}</strong> on <strong>{{ offboardEnv.value }}</strong
            >.
          </p>
          <div v-if="['CLOUDENTE', 'NETWORK'].includes(registrationData.infrastructureServiceApm)">
            <p>Please, keep in mind that before you can offboard application, you should:</p>
            <ul>
              <li v-if="registrationData.infrastructureServiceApm === 'NETWORK'">
                Remove all attached external-dns records (if exists)
              </li>
              <li v-if="registrationData.infrastructureServiceApm === 'CLOUDENTE'">Remove all zones (if exists)</li>
            </ul>
            <p>If above conditions are met, you can proceed with this action.</p>
          </div>
          <div v-if="registrationData.infrastructureServiceApm !== 'CLOUDENTE'">
            <p>
              A SNOW Inquiry will be submitted on your behalf, for <b>IAM</b> team, in order to
              <b>decommission related access products.</b>
            </p>
          </div>
        </q-card-section>
        <q-card-actions class="q-pb-md">
          <q-btn color="primary" @click="offboardRegistration" size="md" class="q-ml-sm" no-caps>Yes</q-btn>
          <q-btn flat size="md" class="btn--no-hover" style="height: 40px" no-caps @click="confirmOffboardPopup = false"
            >No</q-btn
          >
        </q-card-actions>
      </q-card>
    </q-dialog>
  </div>
  <div v-else class="flex items-center justify-center q-py-lg" style="padding: 30px 0">
    <q-spinner-gears size="70px" color="primary"></q-spinner-gears>
  </div>
</template>

<script>
import { JsonForms } from '@jsonforms/vue';
import emitter from 'tiny-emitter/instance';
import { quasarRenderers } from '@/shared/jsonformsQuasarRenderers/renderers';
import {
  createWorkFlow,
  subValWorkflow,
  validateWorkFlow,
  getRelatedWorkflowsStatuses,
  getSummary,
} from '../api/api';
import StatusCell from './registration-network/components/StatusCell.vue';
import { supportCntrlClick } from '@/shared/helpers';
import WorkflowsBtn from '@/shared/components/WorkflowsBtn.vue';
import OndemandButton from '@/shared/components/OndemandButton.vue';
import { getRegistrationEnvironments } from '@/pages/allItems/api/api';

const renderers = [...quasarRenderers];

export default {
  name: 'EnvRegistration',
  components: {
    JsonForms,
    StatusCell,
    WorkflowsBtn,
    OndemandButton,
  },
  props: {
    registrationId: {
      type: String,
    },
    registrationData: {
      type: Object,
    },
    envs: {
      type: Array,
    },
    existEnvironments: {
      type: [Array, undefined],
    },
    mode: {
      type: String,
      default: () => 'read',
    },
    additionalUiSchemaElements: {
      type: Array,
      default: () => [],
    },
    additionalSchemaProperties: {
      type: Object,
      default: () => ({}),
    },
  },
  data() {
    return {
      submitDisable: true,
      offboardEnv: undefined,
      confirmOffboardPopup: false,
      loadData: false,
      showForm: false,
      renderers: Object.freeze(renderers),
      schema: {
        description: 'A set of attributes defining the state of an object',
        type: 'object',
        properties: {
          environments: {
            type: 'array',
            uniqueItems: true,
            items: {
              type: 'string',
              enum: this.envs,
            },
          },
        },
      },
      uiSchema: {
        type: 'VerticalLayout',
        elements: [
          {
            type: 'Control',
            scope: '#/properties/environments',
          },
        ],
      },
      environments: [],
      formData: {
        environments: [],
      },
      addEnvError: undefined,
      disableOffboardEnvs: {},
      formErrors: [],
      errors: [],
      generalErrors: [],
      loadSend: false,
      relatedWorkflowStatuses: [],
      unlockEnvsForOffboard: [],
      loadEnv: undefined,
      summary: undefined,
      loadSummary: false,
      offboardingWikiLink:
        'https://wiki.swissre.com/display/CMP/How+to+offboard+your+Digital+Service+from+Infrastructure+Service',
    };
  },
  computed: {
    newEnvs() {
      return this.formData.environments
        .filter((env) => !this.environments.find((e) => env === e.value))
        .filter((env) => !this.storedEnvironments().find((e) => env === e));
    },
    showSummary() {
      return !['NETWORK'].includes(this.registrationData.infrastructureServiceApm);
    },
    isShowOndemandOffboard() {
      return ['APIMGMT'].includes(this.registrationData.infrastructureServiceApm);
    },
    isShowOffboard() {
      return ['DBAAS', 'MAS', 'APIMGMT', 'OPASS', 'IAASSC', 'ENGPRT'].includes(
        this.registrationData.infrastructureServiceApm,
      );
    },
    offBoardInfraServiceApmId() {
      switch (this.registrationData.infrastructureServiceApm) {
        case 'CLOUDENTE':
          return 'cloudente';
        case 'NETWORK':
          return 'network';
        case 'APIMGMT':
          return 'apimgmt';
        case 'VMWARE':
          return 'vmware';
        case 'OPASS':
          return 'opass';
        case 'CODEWISE':
          return 'codewise';
        case 'MSBI':
          return 'msbi';
        case 'AZAPPHOST':
          return 'azapphost';
        default:
          return 'default';
      }
    },
    infraServiceApmId() {
      switch (this.registrationData.infrastructureServiceApm) {
        case 'CLOUDENTE':
          return 'cloudente';
        case 'APIMGMT':
          return 'apimgmt';
        case 'POSTMAN':
          return 'postman';
        case 'IAASSC':
          return 'iaassc';
        case 'QAAIAGENT':
          return 'qaaiagent';
        case 'CODEWISE':
          return 'codewise';
        case 'ENGPRT':
          return 'engprt';
        case 'AZAPPHOST':
          return 'azapphost';
        default:
          return 'default';
      }
    },
  },
  methods: {
    validateOffboard(env) {
      this.loadEnv = env.value;
      const offboardPayload = this.buildPayload('offboard', env.value);
      validateWorkFlow(offboardPayload)
        .then(() => {
          this.unlockEnvsForOffboard.push(env.value);
        })
        .catch((error) => {
          this.disableOffboardEnvs[env.value] = error.message;
        })
        .finally(() => {
          this.loadEnv = undefined;
        });
    },
    openConfirmationPopUp(env) {
      this.confirmOffboardPopup = true;
      this.offboardEnv = env;
    },
    offboardRegistration() {
      const payload = this.buildPayload('offboard', this.offboardEnv.value);
      createWorkFlow(payload)
        .then(() => {
          this.$notify('positive', 'Success', 'Offboarding started successfull.');
        })
        .catch((error) => {
          this.$notify('negative', 'Error', error.message);
        })
        .finally(async () => {
          this.confirmOffboardPopup = false;
          await this.load();
        });
    },
    storedEnvironments() {
      const storedData = JSON.parse(localStorage.getItem('registrationData')) || {};
      return storedData[this.registrationId] || [];
    },
    showWorkflow(event, id) {
      supportCntrlClick(event, `/workflows?itemId=${id}`);
    },
    cancel() {
      this.showForm = false;
      this.formData.environments = this.environments.map((env) => env.value);
      this.generalErrors = [];
      this.errors = [];
      this.submitDisable = true;
      this.summary = undefined;
      if (this.validationTimeout) clearTimeout(this.validationTimeout);
    },
    buildPayload(action, env) {
      const payload = {
        workflowId: `engprt.${action}-application-${
          action === 'onboard' ? this.infraServiceApmId : this.offBoardInfraServiceApmId
        }`,
        inputVariables: {
          digitalServiceApmId: this.registrationData.digitalServiceApm,
        },
      };
      payload.inputVariables.infraServiceApmId = this.registrationData.infrastructureServiceApm;
      if (env) {
        payload.inputVariables.environmentId = env;
      }
      if (this.formData.reason) {
        payload.inputVariables.reason = this.formData.reason;
      }
      return payload;
    },
    onChange(value) {
      this.formErrors = value.errors;
      if (JSON.stringify(value.data) !== JSON.stringify(this.formData) && !value.errors.length) {
        this.formErrors = value.errors;
        this.formData = value.data;
        this.loadSend = true;
        this.loadSummary = true;
        if (this.validationTimeout) clearTimeout(this.validationTimeout);
        this.validationTimeout = setTimeout(async () => {
          const validationPromises = this.newEnvs.map(async (env) => {
            const payload = this.buildPayload('onboard', env);
            return validateWorkFlow(payload)
              .then(() => {
                this.errors = [];
                this.generalErrors = [];
                this.submitDisable = false;
              })
              .catch((error) => {
                if (error?.response?.data?.errors?.length) {
                  this.errors = error.response.data.errors.filter((error) => error.instancePath.length);
                  this.generalErrors = error.response.data.errors.filter((error) => error.params?.toDisplay) || [];
                } else {
                  this.$notify('negative', 'Error', error.message);
                  this.errors = [];
                  this.generalErrors = [];
                  clearTimeout(this.validationTimeout);
                }
              });
          });

          const allPromises = [...validationPromises];

          if (this.showSummary) {
            const summaryPromise = getSummary({
              digitalServiceApmId: this.registrationData.digitalServiceApm,
              infraServiceApmId: this.registrationData.infrastructureServiceApm,
              environments: this.newEnvs,
            })
              .then((response) => {
                this.summary = response;
              })
              .catch((error) => {
                this.$notify('negative', 'Error', error.message);
              });
            allPromises.push(summaryPromise);
          }

          await Promise.all(allPromises).finally(() => {
            this.loadSend = false;
            this.loadSummary = false;
          });
        }, 3000);
      }
    },
    async load() {
      this.loadData = false;
      try {
        if (Array.isArray(this.existEnvironments)) {
          this.environments = JSON.parse(JSON.stringify(this.existEnvironments));
          this.formData.environments = this.environments.map((env) => env.value);
        } else {
          const data = await getRegistrationEnvironments(
            100,
            0,
            'created_at',
            'ASC',
            true,
            this.registrationId,
            undefined,
            undefined,
            undefined,
            undefined,
            undefined,
            undefined,
            undefined,
          );
          this.formData.environments = data.data.map((env) => env.value);
          this.environments = data.data.map((env) => ({ status: env.status, value: env.value, id: env.id }));
        }
        let storedData = JSON.parse(localStorage.getItem('registrationData')) || {};
        let storedEnvs = storedData[this.registrationId] || [];

        storedData[this.registrationId] = storedEnvs.filter((env) => !this.environments.some((e) => e.value === env));

        if (storedData[this.registrationId].length === 0) {
          delete storedData[this.registrationId];
        }

        this.formData.environments = [...new Set([...this.formData.environments, ...storedEnvs])];

        localStorage.setItem('registrationData', JSON.stringify(storedData));
        if (this.mode === 'edit' && !this.isShowOffboard) {
          const offboardEnvPromises = this.environments.map(async (env) => {
            const offboardPayload = this.buildPayload('offboard', env.value);
            return validateWorkFlow(offboardPayload).catch((error) => {
              this.disableOffboardEnvs[env.value] = error.message;
            });
          });
          const payload = this.buildPayload('onboard');
          payload.subSchemaId = 'add-button';
          const subValPromise = subValWorkflow(payload)
            .then(() => {
              this.addEnvError = undefined;
            })
            .catch((error) => {
              this.addEnvError = error.message;
            });
          await Promise.all([...offboardEnvPromises, subValPromise]);
        }
        const relatedWorkflowsPrmises = this.environments.map((env) => {
          return getRelatedWorkflowsStatuses(env.id).then((response) => {
            this.relatedWorkflowStatuses.push({ id: env.id, statuses: response });
          });
        });
        await Promise.all([...relatedWorkflowsPrmises]);
      } catch (error) {
        this.$notify('negative', 'Error', error.message);
      } finally {
        this.loadData = true;
      }
    },
    async send() {
      this.loadSend = true;
      const promises = this.newEnvs.map(async (env) => {
        const payload = this.buildPayload('onboard', env);
        try {
          await createWorkFlow(payload);
          this.$notify('positive', 'Success', `${env} environment was added successfully.`);
          let storedData = JSON.parse(localStorage.getItem('registrationData')) || {};

          storedData[this.registrationId] = storedData[this.registrationId] || [];
          if (!storedData[this.registrationId].includes(env)) {
            storedData[this.registrationId].push(env);
          }
          localStorage.setItem('registrationData', JSON.stringify(storedData));
        } catch (error) {
          this.$notify('negative', 'Error', error.message);
        }
      });
      await Promise.all(promises).finally(async () => {
        this.loadSend = false;
        this.showForm = false;
        await this.load();
      });
    },
  },
  async mounted() {
    this.schema = {
      ...this.schema,
      properties: {
        ...this.schema.properties,
        ...this.additionalSchemaProperties,
      },
    };
    this.uiSchema = {
      ...this.uiSchema,
      elements: [...this.uiSchema.elements, ...this.additionalUiSchemaElements],
    };
    await this.load();
  },
  created() {
    emitter.on('reloadEnvironments', async () => {
      await this.load();
    });
  },
  unmounted() {
    emitter.off('reloadEnvironments');
  },
};
</script>

<style lang="scss" scoped>
.label-block {
  font-size: 20px;
  font-weight: bold;
}
.btns {
  min-width: 250px;
}
.env-form {
  background-color: var(--q-accent);
  padding-top: 8px;
  margin-bottom: 15px;
  border-top: 1px solid $grey-4;
  border-bottom: 1px solid $grey-4;
}
.environments {
  &.read-widget {
    .block-label {
      margin-bottom: 5px;
    }
    li {
      padding: 10px;
    }
  }
  ul {
    display: flex;
    flex-wrap: wrap;
    margin: 0 -5px;
    padding: 0;
  }

  ul.roles {
    li {
      padding: 3px 0;
      border: none;
    }
  }

  li {
    width: calc(33.333% - 10px);
    margin: 0 5px;
    font-weight: bold;
    padding: 25px;
    border: 1px solid $grey-4;
    border-radius: 10px;
    display: flex;
    align-items: center;

    @media (max-width: $breakpoint-sm-max) {
      width: 100%;
      margin-bottom: 5px;
    }
  }
}
.summary {
  white-space: pre-line;
}
</style>
